/**
 * AssistClient.ts
 *
 * API-Client für den KI-Regelassistenten.
 *
 * Kommuniziert mit dem Backend-Endpunkt POST /api/projects/rules/assist
 * und schickt die natürlichsprachliche Anweisung des Benutzers zusammen
 * mit dem aktuellen rules_xml und dem Gesprächskontext.
 *
 * Wird ausschließlich von Chat.tsx genutzt.
 */
import { apiFetch } from "./baseClient";

/**
 * Eine einzelne Nachricht im Gesprächsverlauf.
 * Wird an das Backend mitgeschickt damit das LLM den Kontext kennt.
 */
export interface AssistMessage {
  /** "user" = Benutzernachricht, "assistant" = LLM-Antwort */
  role: "user" | "assistant";
  content: string;
}

/**
 * Request-Body für den Assistenten-Aufruf (wird von Chat.tsx befüllt).
 */
export interface RulesAssistRequestPayload {
  /** Die natürlichsprachliche Anweisung, z.B. "Herr Müller soll montags frei haben." */
  instruction: string;
  /**
   * Gesprächszustand aus der vorherigen Runde, z.B.:
   *   { active_resource: "Herr Müller", active_day: "Montag" }
   * Wird vom Backend nach jeder Antwort via context_update aktualisiert.
   */
  conversation_state?: Record<string, unknown>;
  /**
   * Konversationshistorie im AssistMessage-Format.
   * Das Backend kürzt daraus den Teil, den es tatsächlich an das Modell schickt.
   */
  history?: AssistMessage[];
  /**
   * Aktuelles rules_xml aus dem Editor.
   * Wenn gesetzt, hat dieses Vorrang vor dem in der Datenbank gespeicherten XML —
   * so werden ungespeicherte Änderungen des Benutzers berücksichtigt.
   */
  rules_xml?: string;
}

/**
 * Antwort des Backends nach einem Assistenten-Aufruf.
 */
export interface RulesAssistResponse {
  /**
   * true wenn das LLM eine Regeländerung vorgenommen hat (mode="mutate"),
   * false bei reiner Antwort/Erklärung (mode="answer").
   */
  changed: boolean;
  /**
   * Strukturierte LLM-Antwort als JSON-Objekt, z.B.:
   *   { mode: "mutate", text: "Ich habe...", operations: [...], context_update: {...} }
   * oder:
   *   { mode: "answer", text: "Welchen Tag möchten Sie..." }
   */
  reply: Record<string, unknown>;
  /** Roher LLM-Ausgabetext vor dem JSON-Parsing (für Debugging). */
  raw_text: string;
  /** Name des genutzten Modells. */
  model: string;
  /**
   * Das geänderte rules_xml nach Anwendung der Operationen.
   * Das Backend liefert normalerweise auch bei mode="answer" das unveränderte XML zurück.
   * Chat.tsx ruft onXmlReceived(xml) nur auf, wenn changed=true und xml gesetzt ist.
   */
  xml?: string | null;
}

/**
 * Sendet eine Benutzeranweisung an den Backend-Regelassistenten.
 *
 * @param userId    ID des eingeloggten Benutzers (für Authentifizierung/Logging)
 * @param projectId ID des aktiven Projekts (Backend liest rules_xml aus DB wenn keins mitgeschickt)
 * @param payload   Anweisung, XML, Verlauf und Kontext
 * @returns         Strukturierte Antwort mit ggf. geändertem XML
 * @throws          Error mit HTTP-Statuscode oder Backend-Fehlermeldung
 */
export async function assistRules(
  userId: number,
  projectId: number,
  payload: RulesAssistRequestPayload
): Promise<RulesAssistResponse> {
  const resp = await apiFetch("/api/projects/rules/assist", {
    method: "POST",
    body: {
      user_id: userId,
      project_id: projectId,
      instruction: payload.instruction,
      conversation_state: payload.conversation_state ?? {},
      history: payload.history ?? [],
      // rules_xml ist optional: wenn nicht gesetzt, liest das Backend aus der DB
      rules_xml: payload.rules_xml,
    } as any,
  });

  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(text || `HTTP ${resp.status}`);
  }

  return (await resp.json()) as RulesAssistResponse;
}
